import { useEffect, useState } from "react";
import { api, formatTL } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, X, Phone, MapPin, ShoppingBag } from "lucide-react";

const empty = { name: "", phone: "", address: "", note: "" };

const CustomerModal = ({ open, onClose, onSaved, editing }) => {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(editing ? { ...empty, ...editing } : empty);
  }, [open, editing]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Ad ve telefon zorunludur");
      return;
    }
    if (form.phone.replace(/\D/g, "").length !== 10) {
      toast.error("Telefon numarası 10 haneli olmalıdır");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/customers/${editing.id}`, form);
        toast.success("Müşteri güncellendi");
      } else {
        await api.post("/customers", form);
        toast.success("Müşteri eklendi");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kaydedilemedi");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[80] grid place-items-center p-4">
      <div onClick={onClose} className="absolute inset-0 bg-black/50" />
      <div
        data-testid="customer-form-modal"
        className="relative bg-white rounded-2xl w-full max-w-md animate-fade-up"
      >
        <header className="flex items-center justify-between px-5 h-16 border-b border-[var(--fb-border)]">
          <h2 className="font-display font-bold text-lg">
            {editing ? "Müşteriyi Düzenle" : "Yeni Müşteri"}
          </h2>
          <button
            data-testid="close-customer-form-btn"
            onClick={onClose}
            className="w-9 h-9 grid place-items-center rounded-full hover:bg-[var(--fb-neutral)]"
          >
            <X className="w-5 h-5" />
          </button>
        </header>
        <form onSubmit={submit} className="p-5 space-y-3">
          <input
            data-testid="customer-form-name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Ad Soyad *"
            className="w-full border border-[var(--fb-border)] rounded-xl px-3 py-2.5 outline-none focus:border-[var(--fb-red)]"
          />
          <input
            data-testid="customer-form-phone"
            value={form.phone}
            inputMode="numeric"
            maxLength={10}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value.replace(/\D/g, "").slice(0, 10) }))}
            placeholder="Telefon * (10 haneli)"
            className="w-full border border-[var(--fb-border)] rounded-xl px-3 py-2.5 outline-none focus:border-[var(--fb-red)]"
          />
          <textarea
            data-testid="customer-form-address"
            value={form.address}
            onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
            placeholder="Adres"
            rows={2}
            className="w-full border border-[var(--fb-border)] rounded-xl px-3 py-2.5 outline-none focus:border-[var(--fb-red)] resize-none"
          />
          <input
            data-testid="customer-form-note"
            value={form.note}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            placeholder="Not (opsiyonel)"
            className="w-full border border-[var(--fb-border)] rounded-xl px-3 py-2.5 outline-none focus:border-[var(--fb-red)]"
          />
          <button
            data-testid="save-customer-btn"
            type="submit"
            disabled={saving}
            className="w-full bg-[var(--fb-red)] hover:bg-[var(--fb-red-hover)] disabled:opacity-60 text-white font-bold py-3 rounded-full transition-colors"
          >
            {saving ? "Kaydediliyor..." : editing ? "Kaydet" : "Müşteri Ekle"}
          </button>
        </form>
      </div>
    </div>
  );
};

const HistoryModal = ({ customer, onClose }) => {
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    if (!customer) return;
    api
      .get(`/customers/${customer.id}/orders`)
      .then((res) => setOrders(res.data))
      .catch(() => setOrders([]));
  }, [customer]);

  if (!customer) return null;

  return (
    <div className="fixed inset-0 z-[80] grid place-items-center p-4">
      <div onClick={onClose} className="absolute inset-0 bg-black/50" />
      <div
        data-testid="customer-history-modal"
        className="relative bg-white rounded-2xl w-full max-w-md max-h-[80vh] overflow-y-auto fb-scroll animate-fade-up"
      >
        <header className="sticky top-0 bg-white flex items-center justify-between px-5 h-16 border-b border-[var(--fb-border)]">
          <div>
            <h2 className="font-display font-bold text-lg">{customer.name}</h2>
            <p className="text-xs text-[var(--fb-muted)]">{customer.phone}</p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 grid place-items-center rounded-full hover:bg-[var(--fb-neutral)]"
          >
            <X className="w-5 h-5" />
          </button>
        </header>
        <div className="p-5 space-y-3">
          {orders === null ? (
            <p className="text-[var(--fb-muted)] text-center py-6">Yükleniyor...</p>
          ) : orders.length === 0 ? (
            <p className="text-[var(--fb-muted)] text-center py-6">Henüz sipariş yok.</p>
          ) : (
            orders.map((o) => (
              <div
                key={o.id}
                className="border border-[var(--fb-border)] rounded-xl p-3 flex items-center justify-between"
              >
                <div>
                  <p className="font-bold text-[var(--fb-red)]">#{o.order_number}</p>
                  <p className="text-xs text-[var(--fb-muted)]">
                    {o.items.reduce((s, i) => s + i.quantity, 0)} ürün · {o.status}
                  </p>
                </div>
                <span className="font-display font-bold">{formatTL(o.total)}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export const CustomersPanel = () => {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [history, setHistory] = useState(null);

  const load = async () => {
    try {
      const res = await api.get("/customers", { params: search ? { search } : {} });
      setCustomers(res.data);
    } catch (e) {
      toast.error("Müşteriler yüklenemedi");
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [search]);

  const handleDelete = async (id) => {
    if (!window.confirm("Bu müşteriyi silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/customers/${id}`);
      toast.success("Müşteri silindi");
      load();
    } catch (e) {
      toast.error("Silme başarısız");
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fb-muted)]" />
          <input
            data-testid="customer-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="İsim veya telefon ara..."
            className="w-full bg-white border border-[var(--fb-border)] rounded-full pl-9 pr-4 py-2 text-sm outline-none focus:border-[var(--fb-red)]"
          />
        </div>
        <button
          data-testid="add-customer-btn"
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
          className="flex items-center gap-2 bg-[var(--fb-red)] hover:bg-[var(--fb-red-hover)] text-white font-bold px-5 py-2.5 rounded-full transition-colors active:scale-95"
        >
          <Plus className="w-5 h-5" /> Yeni Müşteri
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-[var(--fb-border)] overflow-hidden">
        {customers.length === 0 ? (
          <div data-testid="customers-empty" className="p-10 text-center text-[var(--fb-muted)]">
            Kayıtlı müşteri yok. Paket servis siparişleri otomatik olarak buraya eklenir.
          </div>
        ) : (
          <div className="divide-y divide-[var(--fb-border)]">
            {customers.map((c) => (
              <div
                key={c.id}
                data-testid={`customer-row-${c.id}`}
                className="flex items-center gap-4 p-3 sm:p-4 hover:bg-[var(--fb-offwhite)]"
              >
                <div className="w-11 h-11 rounded-full bg-red-50 text-[var(--fb-red)] grid place-items-center font-display font-bold shrink-0">
                  {c.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{c.name}</p>
                  <div className="flex items-center gap-3 text-xs text-[var(--fb-muted)] mt-0.5 flex-wrap">
                    <span className="flex items-center gap-1">
                      <Phone className="w-3 h-3" /> {c.phone}
                    </span>
                    {c.address && (
                      <span className="flex items-center gap-1 truncate max-w-[220px]">
                        <MapPin className="w-3 h-3 shrink-0" /> {c.address}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    data-testid={`customer-history-${c.id}`}
                    onClick={() => setHistory(c)}
                    className="w-9 h-9 grid place-items-center rounded-full hover:bg-[var(--fb-neutral)] text-[var(--fb-muted)]"
                    title="Sipariş geçmişi"
                  >
                    <ShoppingBag className="w-4 h-4" />
                  </button>
                  <button
                    data-testid={`edit-customer-${c.id}`}
                    onClick={() => {
                      setEditing(c);
                      setModalOpen(true);
                    }}
                    className="w-9 h-9 grid place-items-center rounded-full hover:bg-[var(--fb-neutral)] text-[var(--fb-muted)]"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    data-testid={`delete-customer-${c.id}`}
                    onClick={() => handleDelete(c.id)}
                    className="w-9 h-9 grid place-items-center rounded-full hover:bg-red-50 text-[var(--fb-red)]"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <CustomerModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={() => {
          setModalOpen(false);
          load();
        }}
        editing={editing}
      />
      <HistoryModal customer={history} onClose={() => setHistory(null)} />
    </div>
  );
};
